// Service Google Analytics avec gestion du consentement
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-KP34GEBZBP';
const isProduction = import.meta.env.MODE === 'production' || import.meta.env.PROD;

const gtag = (...args) => {
  if (typeof window !== 'undefined' && window.gtag) window.gtag(...args);
};

let gaInitialized = false;
let analyticsAllowed = false;

// Fonction pour vérifier le consentement
const checkAnalyticsConsent = () => {
  try {
    const consent = localStorage.getItem('cookie-consent-preferences');
    if (consent) {
      const preferences = JSON.parse(consent);
      return preferences.analytics === true;
    }
    return false;
  } catch (error) {
    console.error('Erreur lors de la vérification du consentement:', error);
    return false;
  }
};

// Écouter les changements de préférences cookies
if (typeof window !== 'undefined') {
  window.addEventListener('cookiePreferencesChanged', (event) => {
    const preferences = event.detail;
    analyticsAllowed = preferences.analytics;
    
    if (analyticsAllowed && !gaInitialized) {
      // L'utilisateur a accepté les cookies analytiques
      initGA();
    } else if (!analyticsAllowed && gaInitialized) {
      // L'utilisateur a refusé les cookies analytiques
      disableGA();
    }
  });
}

// Désactiver Google Analytics
const disableGA = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Désactiver la collecte de données
    gtag('consent', 'update', {
      analytics_storage: 'denied'
    });
    console.log('Google Analytics désactivé suite au refus des cookies');
  }
};

// Appeler UNE SEULE FOIS au boot de l'app
export const initGA = () => {
  if (gaInitialized) return;
  
  // Vérifier le consentement avant d'initialiser
  analyticsAllowed = checkAnalyticsConsent();
  
  if (!analyticsAllowed) {
    console.log('GA: Consentement requis pour les cookies analytiques');
    return;
  }
  
  if (!isProduction) { 
    console.log('GA désactivé en dev'); 
    gaInitialized = true; 
    return; 
  }
  
  if (!GA_MEASUREMENT_ID) { 
    console.warn('GA: ID manquant'); 
    return; 
  }

  if (typeof window !== 'undefined') {
    // Ne réinsère pas le script si déjà présent
    if (!window.gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function(){ window.dataLayer.push(arguments); };
    }

    // Initialisation GA4 avec consentement
    gtag('js', new Date());
    
    // Configurer le consentement
    gtag('consent', 'default', {
      analytics_storage: analyticsAllowed ? 'granted' : 'denied',
      ad_storage: 'denied', // Toujours refusé par défaut
      ad_user_data: 'denied',
      ad_personalization: 'denied'
    });

    // Config GA4
    gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      cookie_flags: 'SameSite=Lax;Secure',
      cookie_update: false,
      anonymize_ip: true // Anonymiser les IP pour la conformité RGPD
    });

    gaInitialized = true;
    if (!isProduction) console.log('Google Analytics initialisé avec consentement');
  }
};

// Page views (à appeler sur chaque navigation)
export const trackPageView = (url, title) => {
  // Vérifier le consentement avant de tracker
  if (!checkAnalyticsConsent()) {
    if (!isProduction) console.log('[GA] Page view non trackée - consentement requis');
    return;
  }
  
  if (!isProduction || !GA_MEASUREMENT_ID) {
    console.log('[GA Dev page_view]', url, title);
    return;
  }
  
  try {
    gtag('event', 'page_view', {
      page_title: title,
      page_location: window.location.origin + url,
      page_path: url
    });
  } catch (e) {
    console.error('GA: Erreur page_view', e);
  }
};

// Events génériques
export const trackEvent = (action, category = 'general', label = '', value = 0) => {
  // Vérifier le consentement avant de tracker
  if (!checkAnalyticsConsent()) {
    if (!isProduction) console.log('[GA] Event non tracké - consentement requis');
    return;
  }
  
  if (!isProduction || !GA_MEASUREMENT_ID) {
    console.log('[GA Dev event]', action, category, label, value);
    return;
  }
  
  try {
    gtag('event', action, {
      send_to: GA_MEASUREMENT_ID,
      event_category: category,
      event_label: label,
      value
    });
  } catch (e) {
    console.error('GA: Erreur event', e);
  }
};

// Raccourcis métiers
export const analytics = {
  courseStarted: (courseName) => trackEvent('course_started', 'education', courseName),
  courseCompleted: (courseName) => trackEvent('course_completed', 'education', courseName),
  codeExecuted: (projectType) => trackEvent('code_executed', 'coding', projectType),
  userLogin: (method) => gtag('event', 'login', { send_to: GA_MEASUREMENT_ID, method }),
  passCodeurPurchased: () => trackEvent('purchase', 'monetization', 'pass_codeur')
};

export default analytics;
